import React, { forwardRef, useState } from "react";
import { Building2, BadgeCheck, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { t, dateLocale } from "i18n";
import UserDetailModal from "./UserDetailModal";

const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.id || null;
  } catch {
    return null;
  }
};

const formatLastLogin = (value) => {
  if (!value) return t("admin.superUser.userRow.never");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(dateLocale("en-US"), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getInitials = (f, l) =>
  ((f?.[0] || "") + (l?.[0] || "")).toUpperCase() || "?";

const UserRow = forwardRef(({ user: initialUser, isLast, onDeleted }, ref) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(initialUser);
  const [showModal, setShowModal] = useState(false);

  const fullName =
    [user.fname, user.lname].filter(Boolean).join(" ") ||
    t("common.status.unknown");
  const workspaceCount = user.workspace_count ?? user.workspaces?.length ?? 0;

  return (
    <>
      <div
        ref={ref}
        onClick={() => setShowModal(true)}
        className={`flex cursor-pointer items-center justify-between px-4 py-3 transition duration-150 hover:bg-[#FAFAFA] ${
          !isLast ? "border-b border-[#F3F4F6]" : ""
        }`}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#E07A3A]">
            <span className="text-xs font-semibold text-white">
              {getInitials(user.fname, user.lname)}
            </span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-sm font-medium text-[#111827]">
                {fullName}
              </p>
              <BadgeCheck
                size={13}
                className={
                  user.is_verified
                    ? "flex-shrink-0 text-[#16A34A]"
                    : "flex-shrink-0 text-[#D1D5DB]"
                }
                title={
                  user.is_verified
                    ? t("admin.superUser.userRow.verified")
                    : t("admin.superUser.userRow.unverified")
                }
              />
            </div>
            <p className="truncate text-xs text-[#6B7280]">{user.email}</p>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-6">
          {user.is_superadmin ? (
            <span className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold text-[#DC2626]">
              <ShieldCheck size={11} />
              {t("admin.superUser.userRow.superAdmin")}
            </span>
          ) : (
            <span className="rounded bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-medium text-[#6B7280]">
              {t("admin.superUser.userRow.member")}
            </span>
          )}
          <div className="hidden items-center gap-1.5 text-xs text-[#6B7280] sm:flex">
            <Building2 size={12} />
            <span>
              {t("admin.superUser.userRow.workspaceCount", {
                total: workspaceCount,
              })}
            </span>
          </div>
          <span
            className="w-28 text-right text-xs text-[#9CA3AF]"
            title={t("admin.superUser.userRow.lastLogin")}
          >
            {formatLastLogin(user.last_login_at)}
          </span>
        </div>
      </div>

      {showModal && (
        <UserDetailModal
          userId={user.id}
          show={showModal}
          onClose={() => setShowModal(false)}
          currentUserId={currentUserId()}
          onChanged={(updated) => {
            if (updated === null) {
              // Deleted — ask the list to refetch.
              onDeleted?.();
            } else {
              // Edited — reflect the changes in this row without a refetch.
              setUser((prev) => ({ ...prev, ...updated }));
            }
          }}
        />
      )}
    </>
  );
});

UserRow.displayName = "UserRow";

export default UserRow;
